'use server'

import { apiRequest, ApiResponse } from '@/lib/api/request'

export type FiscalNoteType = 'NFE' | 'NFSE'
export type FiscalNoteStatus = 'PENDING' | 'PROCESSING' | 'AUTHORIZED' | 'DENIED' | 'CANCELLED' | 'ERROR'

export interface FiscalNote {
    id: number
    companyId: number
    createdById?: number
    type: FiscalNoteType
    status: FiscalNoteStatus
    ref: string
    numero?: string
    serie?: string
    chaveNfe?: string
    protocolo?: string
    statusSefaz?: string
    mensagemSefaz?: string
    numeroRps?: string
    serieRps?: string
    codigoVerificacao?: string
    xmlUrl?: string
    pdfUrl?: string
    valorTotal: number
    clientId?: number
    client?: { id: number; name: string; cpf?: string }
    destinatarioCnpjCpf?: string
    destinatarioNome?: string
    descricao?: string
    requestPayload?: any
    responsePayload?: any
    errorMessage?: string
    dataEmissao?: string
    billingId?: number
    billing?: {
        id: number
        status: string
        amount: number
        paidAmount?: number
        paymentMethod?: string
        dueDate: string
        paidAt?: string
    }
    createdAt: string
    updatedAt: string
    createdBy?: { id: number; name: string }
}

export interface NfeItem {
    productId?: number
    descricao: string
    codigoProduto: string
    cfop: string
    unidadeComercial: string
    quantidadeComercial: number
    valorUnitarioComercial: number
    icmsOrigem?: number
    icmsSituacaoTributaria?: string
    icmsAliquota?: number
    icmsBaseCalculo?: number
    pisAliquota?: number
    cofinsAliquota?: number
}

export interface EnderecoData {
    logradouro: string
    numero: string
    complemento?: string
    bairro: string
    codigoMunicipio: string
    uf: string
    cep: string
}

export interface CreateNfeData {
    naturezaOperacao: string
    tipoDocumento?: number
    finalidadeEmissao?: number
    clientId?: number
    destinatarioCnpjCpf: string
    destinatarioRazaoSocial: string
    destinatarioEmail?: string
    destinatarioEndereco?: EnderecoData
    destinatarioInscricaoEstadual?: string
    items: NfeItem[]
    informacoesAdicionais?: string
}

export interface CreateNfseData {
    valorServicos: number
    discriminacao: string
    issRetido: boolean
    itemListaServico: string
    codigoTributarioMunicipio?: string
    aliquota?: number
    serviceType?: string
    serviceId?: number
    clientId?: number
    tomadorCnpjCpf: string
    tomadorRazaoSocial: string
    tomadorEmail?: string
    tomadorTelefone?: string
    tomadorEndereco?: EnderecoData
    naturezaOperacao?: string
}

export interface CompanyCertificate {
    id: number
    serialNumber: string
    issuer: string
    subject: string
    cnpj: string
    validFrom: string
    validUntil: string
    status: string
    createdAt: string
}

export const FISCAL_NOTE_TYPE_LABELS: Record<FiscalNoteType, string> = {
    NFE: 'NF-e',
    NFSE: 'NFS-e',
}

export const FISCAL_NOTE_STATUS_LABELS: Record<FiscalNoteStatus, string> = {
    PENDING: 'Pendente',
    PROCESSING: 'Processando',
    AUTHORIZED: 'Autorizada',
    DENIED: 'Rejeitada',
    CANCELLED: 'Cancelada',
    ERROR: 'Erro',
}

export const FISCAL_NOTE_STATUS_COLORS: Record<FiscalNoteStatus, string> = {
    PENDING: 'badge-ghost',
    PROCESSING: 'badge-warning',
    AUTHORIZED: 'badge-success',
    DENIED: 'badge-error',
    CANCELLED: 'badge-ghost',
    ERROR: 'badge-error',
}

// Readiness
export interface FiscalReadiness {
    /**
     * `false` em conta de pessoa física: emitir nota exige inscrição no fisco com
     * CNPJ. Quando `false`, `nfe`/`nfse` vêm sempre `ready: false` e sem lista de
     * pendências — não há campo que o usuário possa preencher para habilitar.
     */
    supported: boolean
    isProvisioned: boolean
    serviceListItem: string | null
    nfe: { ready: boolean; missingFields: string[] }
    nfse: { ready: boolean; missingFields: string[] }
}

export const getFiscalReadiness = async (): Promise<ApiResponse<FiscalReadiness>> => {
    return apiRequest<FiscalReadiness>('fiscal/readiness', {})
}

// Notes CRUD
export const getNotes = async (params?: {
    type?: FiscalNoteType
    status?: FiscalNoteStatus
    search?: string
    page?: number
    limit?: number
}): Promise<ApiResponse<FiscalNote[]>> => {
    return apiRequest<FiscalNote[]>('fiscal/notes', { params })
}

export const getNote = async (id: number): Promise<ApiResponse<FiscalNote>> => {
    return apiRequest<FiscalNote>('fiscal/notes', { id: String(id) })
}

export const createNfe = async (data: CreateNfeData): Promise<ApiResponse<FiscalNote>> => {
    return apiRequest<FiscalNote>('fiscal/notes/nfe', { data }, 'post')
}

export const createNfse = async (data: CreateNfseData): Promise<ApiResponse<FiscalNote>> => {
    return apiRequest<FiscalNote>('fiscal/notes/nfse', { data }, 'post')
}

export const cancelNote = async (id: number, justificativa: string): Promise<ApiResponse<FiscalNote>> => {
    return apiRequest<FiscalNote>(`fiscal/notes/${id}/cancel`, { data: { justificativa } }, 'post')
}

export const syncNote = async (id: number): Promise<ApiResponse<FiscalNote>> => {
    return apiRequest<FiscalNote>(`fiscal/notes/${id}/sync`, {}, 'post')
}

export const deleteNote = async (id: number): Promise<ApiResponse<any>> => {
    return apiRequest<any>('fiscal/notes', { id: String(id) }, 'delete')
}

// Certificates
export const getCertificates = async (): Promise<ApiResponse<CompanyCertificate[]>> => {
    return apiRequest<CompanyCertificate[]>('fiscal/certificates', {})
}

export const uploadCertificate = async (file: File, password: string): Promise<ApiResponse<CompanyCertificate>> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('password', password)
    return apiRequest<CompanyCertificate>('fiscal/certificates/upload', { data: formData, formData: true }, 'post')
}

export const deleteCertificate = async (id: number): Promise<ApiResponse<any>> => {
    return apiRequest<any>('fiscal/certificates', { id: String(id) }, 'delete')
}

// Preferences
export interface FiscalPreferences {
    autoGenerateInvoices: boolean
    autoSendEmail: boolean
}

export const getFiscalPreferences = async (): Promise<ApiResponse<FiscalPreferences>> => {
    return apiRequest<FiscalPreferences>('fiscal/preferences', {})
}

export const updateFiscalPreferences = async (data: Partial<FiscalPreferences>): Promise<ApiResponse<FiscalPreferences>> => {
    return apiRequest<FiscalPreferences>('fiscal/preferences', { data }, 'patch')
}
